import { Chip, Typography, makeStyles, Grid } from '@material-ui/core'
import clsx from 'clsx'
import React, { useState } from 'react'
import { Link } from 'react-router-dom'

import { useIsMobile } from 'src/modules/ui/utils'
import { formatPrice } from 'src/utils'

const useStyles = makeStyles(theme => ({
  chipUnavailable: {
    marginLeft: theme.spacing(1),
  },
  productCell: {
    paddingBottom: theme.spacing(0),
    '&:last-child': {
      paddingBottom: theme.spacing(1),
    },
    [theme.breakpoints.up('mobile')]: {
      paddingBottom: theme.spacing(1),
    },
  },
  productNameCell: {
    paddingBottom: theme.spacing(0),
    [theme.breakpoints.up('mobile')]: {
      paddingBottom: theme.spacing(1),
    },
    flex: '0 0 auto',
    width: 400,
  },
  productNameContainer: {
    display: 'flex',
    alignItems: 'center',
    paddingRight: theme.spacing(2),
  },
  productNameContainerMobile: {
    paddingBottom: theme.spacing(0.5),
  },
  productPriceContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    whiteSpace: 'nowrap',
  },
  productDescription: {
    display: 'box',
    lineClamp: 1,
    maxHeight: 20,
    boxOrient: 'vertical',
    overflow: 'hidden',
  },
  productInfo: {
    fontFamily: 'Gotham',
    textTransform: 'uppercase',
    fontWeight: 500,
    fontSize: '0.875rem',
    whiteSpace: 'pre-wrap',
  },
  productSupplierName: {
    fontFamily: 'Gotham',
    textTransform: 'uppercase',
    fontSize: '0.875rem',
  },
  productCode: {
    fontFamily: 'Gotham',
    textTransform: 'uppercase',
    fontSize: '0.875rem',
    marginRight: theme.spacing(1),
    fontWeight: 500,
  },
  productLink: {
    alignItems: 'center',
    color: 'inherit',
    textDecoration: 'inherit',
    display: 'flex',
  },
  productPhoto: {
    borderRadius: theme.shape.borderRadius,
    height: 40,
    objectFit: 'cover',
    marginRight: theme.spacing(1),
    width: 40,
  },
  productPrice: {
    paddingLeft: theme.spacing(2),
    textAlign: 'right',
  },
  productSubtotal: {
    paddingLeft: theme.spacing(2),
    textAlign: 'right',
  },
  fieldOverriden: {
    textDecoration: 'line-through',
    color: theme.palette.lightPurple.main,
  },
  productRow: {
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up('mobile')]: {
      display: 'table-row',
      '& td': {
        verticalAlign: 'bottom',
      },
    },
  },
  productRowCentered: {
    '& td': {
      verticalAlign: 'middle',
    },
  },
  productRowExcluded: {
    textDecoration: 'line-through',
    color: theme.palette.lightPurple.main,
  },
  productUnit: {
    paddingLeft: theme.spacing(2),
    fontWeight: 'normal',
    textAlign: 'right',
  },
  productUnitMobile: {
    paddingLeft: 0,
    width: 'auto',
  },
  productMobile: {
    marginBottom: theme.spacing(2.5),
  },
  productMobilePre: {
    marginRight: theme.spacing(0.5),
  },
  productMobileTop: {
    display: 'flex',
    flexDirection: 'row',
  },
  productMobileTopWrap: {
    color: 'inherit',
    display: 'flex',
    flexDirection: 'row',
    textDecoration: 'inherit',
  },
  productMobileBottom: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  productSupplierNameCell: {
    width: 150,
    [theme.breakpoints.up('laptop')]: {
      width: 200,
    },
  },
  productPriceCell: {
    width: 75,
  },
  productUnitCell: {
    width: 150,
  },
}))

const ProductListItem = props => {
  const isMobile = useIsMobile()

  const [showSupplementalComponent, setShowSupplementalComponent] = useState({})

  const handleSupplementalComponentChange = e => {
    setShowSupplementalComponent({
      ...showSupplementalComponent,
      [e.target.name]: e.target.checked,
    })
  }

  return isMobile ? (
    <ProductMobile
      {...props}
      showSupplementalComponent={showSupplementalComponent}
      handleSupplementalComponentChange={handleSupplementalComponentChange}
    />
  ) : (
    <Product
      {...props}
      showSupplementalComponent={showSupplementalComponent}
      handleSupplementalComponentChange={handleSupplementalComponentChange}
    />
  )
}

const Product = ({
  linkPrefix,
  navigateOnClick,
  productPreCell,
  productPostCell,
  showDescription,
  showSupplierName,
  showPhoto,
  showPrices,
  showQuantity,
  showSubtotal,
  rootProps = {},
  conditionalSupplementalComponent = null,
  showSupplementalComponent = {},
  handleSupplementalComponentChange = () => {},
  ...product
}) => {
  const classes = useStyles()

  const Wrap =
    navigateOnClick && product.id ? Link : props => <div {...props} />
  return (
    <>
      <tr
        className={clsx(classes.productRow, {
          [classes.productRowExcluded]: product.excludeFromOrder,
          [classes.productRowCentered]: productPostCell,
        })}
        {...rootProps}
      >
        {productPreCell && (
          <td className={classes.productCell}>{productPreCell({ product })}</td>
        )}
        <td className={classes.productNameCell}>
          <Wrap
            to={`${linkPrefix}/products/${product.id}`}
            className={classes.productLink}
            tabIndex={-1}
          >
            {product.code ? (
              <td className={classes.productCode}>{product.code}</td>
            ) : null}
            {showPhoto && product.photo && <ProductPhoto product={product} />}
            <div>
              <ProductName product={product} />
              {showDescription && <ProductDescription product={product} />}
            </div>
          </Wrap>
        </td>
        {showSupplierName && (
          <td
            className={clsx(
              classes.productCell,
              classes.productSupplierNameCell
            )}
          >
            <Typography className={classes.productSupplierName}>
              {product.supplier.name}
            </Typography>
          </td>
        )}
        <td className={clsx(classes.productCell, classes.productUnitCell)}>
          <ProductUnit product={product} />
        </td>
        {showPrices && (
          <td className={clsx(classes.productCell, classes.productPriceCell)}>
            {!product.variablePrice && <ProductPrice product={product} />}
            {product.variablePrice && (
              <ProductVariablePrice product={product} />
            )}
          </td>
        )}
        {showQuantity && (
          <td className={classes.productCell}>
            <ProductQuantity product={product} />
          </td>
        )}
        {showSubtotal && (
          <td className={classes.productCell}>
            <ProductSubtotal product={product} />
          </td>
        )}
        {productPostCell && (
          <td className={classes.productCell}>
            {productPostCell({ product })}
          </td>
        )}
      </tr>
      {conditionalSupplementalComponent ? (
        <tr>
          <td colSpan={99}>{conditionalSupplementalComponent}</td>
        </tr>
      ) : null}

      {!conditionalSupplementalComponent && product.disputeReason ? (
        <tr>
          <td>
            <Typography color="secondary" variant="body2" gutterBottom>
              {product.disputeReason}
            </Typography>
          </td>
        </tr>
      ) : null}
      {/* This is used for invoice orders line items */}
      {product.lineItemDisputeReason ? (
        <tr>
          <td>
            <Typography color="secondary" variant="body2" gutterBottom>
              {product.lineItemDisputeReason}
            </Typography>
          </td>
        </tr>
      ) : null}
    </>
  )
}

const ProductMobile = ({
  linkPrefix,
  navigateOnClick,
  productPreCell,
  productPostCell,
  showDescription,
  showSupplierName,
  showPhoto,
  showPrices,
  showQuantity,
  showSubtotal,
  rootProps,
  conditionalSupplementalComponent = null,
  showSupplementalComponent = {},
  handleSupplementalComponentChange = () => {},
  ...product
}) => {
  const classes = useStyles()
  const Wrap =
    navigateOnClick && product.id ? Link : props => <div {...props} />

  return (
    <tr {...rootProps}>
      <td>
        <div className={classes.productMobile}>
          <div className={classes.productMobileTop}>
            {productPreCell && (
              <div className={classes.productMobilePre}>
                {productPreCell({ product })}
              </div>
            )}
            <Wrap
              className={classes.productMobileTopWrap}
              tabIndex={-1}
              to={`${linkPrefix}/products/${product.id}`}
            >
              {showPhoto && product.photo && <ProductPhoto product={product} />}
              <div className={classes.productMobileInfo}>
                <ProductName product={product} mobile />
                {showSupplierName && (
                  <Typography className={classes.productSupplierName}>
                    {product.supplier.name}
                  </Typography>
                )}
                {showDescription && <ProductDescription product={product} />}
              </div>
            </Wrap>
          </div>
          {conditionalSupplementalComponent ? (
            <tr>
              <td colSpan={99}>{conditionalSupplementalComponent}</td>
            </tr>
          ) : null}

          {!conditionalSupplementalComponent && product.disputeReason ? (
            <tr>
              <td>
                <Typography color="secondary" variant="body2" gutterBottom>
                  {product.disputeReason}
                </Typography>
              </td>
            </tr>
          ) : null}
          <div className={classes.productMobileBottom}>
            <Grid container spacing={2}>
              <Grid xs={6}>
                <ProductUnit product={product} mobile />
              </Grid>
              {showPrices && (
                <Grid xs={6}>
                  <ProductPrice product={product} />
                </Grid>
              )}
              {showQuantity && (
                <Grid xs={6}>
                  <ProductQuantity product={product} />
                </Grid>
              )}
              {showSubtotal && (
                <Grid xs={6}>
                  <ProductSubtotal product={product} />
                </Grid>
              )}
              {productPostCell && (
                <Grid xs={12}>
                  <div>{productPostCell({ product })}</div>
                </Grid>
              )}
            </Grid>
          </div>
          {product.lineItemDisputeReason ? (
            <Typography color="secondary" variant="body2" gutterBottom>
              {product.lineItemDisputeReason}
            </Typography>
          ) : null}
        </div>
      </td>
    </tr>
  )
}

const ProductName = ({ product, mobile }) => {
  const { excludeFromOrder, name, updatedProductName } = product
  const classes = useStyles()
  return (
    <div
      className={clsx(classes.productNameContainer, {
        [classes.productNameContainerMobile]: mobile,
      })}
    >
      <Typography component="div" className={classes.productInfo}>
        <span className={updatedProductName && classes.fieldOverriden}>
          {name}
        </span>
        {!excludeFromOrder && updatedProductName && (
          <div>{`${updatedProductName}`}</div>
        )}
      </Typography>
      {!product.available && (
        <Chip
          label="Unavailable"
          size="small"
          className={classes.chipUnavailable}
        />
      )}
    </div>
  )
}

const ProductDescription = ({ product }) => {
  const classes = useStyles()
  return (
    <Typography variant="body2" className={classes.productDescription}>
      {product.description}
    </Typography>
  )
}

const ProductPhoto = ({ product }) => {
  const classes = useStyles()
  return (
    <img
      src={product.photo}
      alt={product.name}
      className={classes.productPhoto}
    />
  )
}

const ProductUnit = ({ mobile, product }) => {
  const classes = useStyles()
  const {
    avEquivalentUnitSize,
    excludeFromOrder,
    updatedProductUnit,
    variablePrice,
    variablePriceUnit,
  } = product

  let secondRow = variablePrice && avEquivalentUnitSize && (
    <div>{`(~${avEquivalentUnitSize}${variablePriceUnit})`}</div>
  )
  if (!excludeFromOrder && updatedProductUnit) {
    secondRow = <div>{`${updatedProductUnit}`}</div>
  }

  return (
    <Typography
      variant="body2"
      component="div"
      className={clsx(classes.productInfo, classes.productUnit, {
        [classes.productUnitMobile]: mobile,
      })}
    >
      <span className={updatedProductUnit && classes.fieldOverriden}>
        {product.unit}
      </span>
      {secondRow}
    </Typography>
  )
}

const ProductPrice = ({ product }) => {
  const classes = useStyles()
  const { overridePrice, price, updatedProductPrice } = product

  return (
    <div className={classes.productPriceContainer}>
      {price != null && (
        <Typography
          variant="body2"
          component="div"
          className={clsx(classes.productInfo, classes.productPrice, {
            [classes.fieldOverriden]: overridePrice != null,
          })}
        >
          <span className={updatedProductPrice && classes.fieldOverriden}>
            {formatPrice(price)}
          </span>
          {updatedProductPrice && <div>{formatPrice(updatedProductPrice)}</div>}
        </Typography>
      )}

      {overridePrice != null && (
        <Typography
          variant="body2"
          className={clsx(classes.productInfo, classes.productPrice)}
        >
          {formatPrice(overridePrice)}
        </Typography>
      )}
    </div>
  )
}

const ProductVariablePrice = ({ product }) => {
  const classes = useStyles()
  const {
    avEquivalentUnitSize,
    excludeFromOrder,
    overridePrice,
    price,
    updatedProductPrice,
    updatedProductUnit,
    variablePrice,
    variablePriceUnit,
  } = product

  const discardedPricePerUnit =
    !excludeFromOrder && updatedProductPrice
      ? `(${formatPrice(price)}/${variablePriceUnit})`
      : null

  const actualPrice = updatedProductPrice || price

  const pricePerUnit = `(${formatPrice(actualPrice)}/${variablePriceUnit})`

  const totalPrice =
    avEquivalentUnitSize && !updatedProductUnit
      ? formatPrice(actualPrice * avEquivalentUnitSize)
      : null

  return (
    <div className={classes.productPriceContainer}>
      {actualPrice != null && (
        <Typography
          variant="body2"
          component="div"
          className={clsx(classes.productInfo, classes.productPrice, {
            [classes.fieldOverriden]: overridePrice != null,
          })}
        >
          {discardedPricePerUnit && (
            <div className={classes.fieldOverriden}>
              {discardedPricePerUnit}
            </div>
          )}
          {totalPrice && <div>{totalPrice}</div>}
          {pricePerUnit && <div>{pricePerUnit}</div>}
        </Typography>
      )}

      {overridePrice != null && (
        <Typography
          variant="body2"
          className={clsx(classes.productInfo, classes.productPrice)}
        >
          {formatPrice(overridePrice * avEquivalentUnitSize)}
          {variablePrice && (
            <div>{`(${formatPrice(overridePrice)}/${variablePriceUnit})`}</div>
          )}
        </Typography>
      )}
    </div>
  )
}

const ProductQuantity = ({ product }) => {
  console.log(product)
  const classes = useStyles()
  const {
    quantity,
    avEquivalentUnitSize,
    excludeFromOrder,
    totalSizeSent,
    updatedQuantity,
    variablePrice,
    variablePriceUnit,
  } = product

  const totalSize =
    variablePrice && totalSizeSent
      ? totalSizeSent
      : variablePrice && avEquivalentUnitSize
      ? avEquivalentUnitSize * quantity
      : null

  return (
    <Typography
      variant="body2"
      component="div"
      className={clsx(classes.productInfo, classes.productPrice)}
    >
      <span className={updatedQuantity && classes.fieldOverriden}>
        Qty. {quantity}
      </span>
      {!excludeFromOrder && updatedQuantity && (
        <div>{`QTY. ${updatedQuantity}`}</div>
      )}
      {variablePrice && totalSize && (
        <div>{`(${
          !totalSizeSent ? '~' : ''
        }${totalSize.toFixed(3)}${variablePriceUnit})`}</div>
      )}
    </Typography>
  )
}

const ProductSubtotal = ({ product }) => {
  const classes = useStyles()
  const {
    quantity,
    avEquivalentUnitSize,
    totalSizeSent,
    variablePrice,
    price,
  } = product

  const totalSize =
    variablePrice && totalSizeSent
      ? totalSizeSent
      : variablePrice && avEquivalentUnitSize
      ? avEquivalentUnitSize * quantity
      : quantity

  const subtotal = price * totalSize

  return (
    <Typography
      variant="body1"
      component="div"
      className={clsx(classes.productSubtotal)}
    >
      <span>{formatPrice(subtotal)}</span>
    </Typography>
  )
}

export default ProductListItem
